// Core Setback game types. The vocabulary used by the engine, AI, and UI.

// Ruleset variants the engine supports. Locked at match-start time so rules
// can't change mid-game.
//   - "tpc":      TPC Tournament Rules. Trump can be played at any time.
//                 First team to 11 wins (bidder team wins ties).
//   - "vacation": Vacation rules. Same trump-anytime rule as TPC, but adds
//                 two house twists: "coke" hands (all cards 3–9) can be
//                 claimed for a redeal (claimant forfeits the bid), and the
//                 non-bidding team can only go out at 11 on a hand their
//                 own team bid — their score clamps at 10 otherwise.
export type Ruleset = "tpc" | "vacation";
export const RULESETS: Ruleset[] = ["tpc", "vacation"];
export const DEFAULT_RULESET: Ruleset = "tpc";

export type Suit = "C" | "D" | "H" | "S"; // Clubs, Diamonds, Hearts, Spades
export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14; // 11=J, 12=Q, 13=K, 14=A

export interface Card {
  suit: Suit;
  rank: Rank;
}

export type SeatIndex = 0 | 1 | 2 | 3;
export type TeamId = "A" | "B";

// Seats 0 & 2 are Team A, seats 1 & 3 are Team B. Partners sit across.
export const TEAM_OF: Record<SeatIndex, TeamId> = { 0: "A", 1: "B", 2: "A", 3: "B" };

export type BidValue = 2 | 3 | 4;
export interface Bid {
  seat: SeatIndex;
  value: BidValue | "pass";
}

export interface Trick {
  leader: SeatIndex;
  plays: { seat: SeatIndex; card: Card }[];
  winner?: SeatIndex;
}

export type Phase = "deal" | "bidding" | "choosingTrump" | "playing" | "scoring" | "done";

export interface HandScore {
  teamA: number;
  teamB: number;
  high?: TeamId;
  low?: TeamId;
  jack?: TeamId;
  game?: TeamId;
  // Raw "game point" totals each team accumulated this hand (J=1, Q=2, K=3,
  // A=4, 10=10 per captured card). The `game` field above records who *won*
  // game; these expose the tally so players can see by how much.
  gamePointsA: number;
  gamePointsB: number;
  bidderMade: boolean;
}

export interface HandState {
  handNumber: number;
  dealer: SeatIndex;
  hands: Card[][]; // 4 arrays, one per seat
  bids: Bid[]; // in order
  bidder?: SeatIndex;
  bid?: BidValue;
  trump?: Suit;
  tricks: Trick[];
  currentTrick?: Trick;
  turn?: SeatIndex;
  phase: Phase;
  score?: HandScore;
  capturedCards: { A: Card[]; B: Card[] };
  eventLog: GameEvent[];
  // Seats that claimed a "coke hand" (vacation ruleset only). They were dealt
  // 6 new cards and are forbidden from bidding this hand.
  cokeClaimed?: SeatIndex[];
  // Set when every seat passed or claimed coke — nobody bid, no score change,
  // deal the next hand with normal dealer rotation.
  abandoned?: boolean;
  // Undealt portion of the shuffled deck (28 cards after initial deal, less
  // 6 per coke claim). Single-player mode stores it here for convenience;
  // multiplayer keeps it server-side only so clients never see unplayed
  // cards. Omitted from the public projection in the online path.
  deckRemaining?: Card[];
}

export interface GameState {
  seed: number;
  ruleset: Ruleset;
  players: {
    seat: SeatIndex;
    uid?: string; // undefined if bot
    screenName: string;
    botDifficulty?: "advanced" | "expert";
  }[];
  scoreA: number;
  scoreB: number;
  currentHand: HandState;
  completedHands: HandState[];
  winner?: TeamId;
}

// Event log — every action in a hand. Replay = re-run engine over log.
export type GameEvent =
  | { type: "deal"; seed: number; dealer: SeatIndex }
  | { type: "bid"; seat: SeatIndex; value: BidValue | "pass" }
  | { type: "forcedBid"; seat: SeatIndex }
  | { type: "chooseTrump"; seat: SeatIndex; trump: Suit }
  | { type: "play"; seat: SeatIndex; card: Card }
  | { type: "trickWon"; winner: SeatIndex; cards: Card[] }
  | { type: "score"; score: HandScore }
  | { type: "cokeClaim"; seat: SeatIndex }
  | { type: "abandoned" };
